import { AnyUser } from '@/components/OrgAdminProjectSettingsUserTable/types';
import MatTable from '@/components/Utils/MatTable';
import {
  changeProjectVisibilityApi,
  deleteUserFromProjectApi,
  transferProjectOwnershipApi,
} from '@/hooks/api/useOrgAdminApi';
import { useMenu } from '@/hooks/useMenu';
import { useTypedSelector } from '@/hooks/useTypedSelector';
import LoadingProgress from '@/pages/model_iteration/componentsV2/LoadingProgress';
import {
  useGetOrgAdminProjectDetailsQuery,
  useGetProjectStatsByOrgIdQuery,
  useOrgAdminGetProjectsQuery,
} from '@/serverStore/projects';
import { IconButton } from '@clef/client-library';
import {
  LabelType,
  GroupUser,
  Project,
  ProjectId,
  ProjectRole,
  ProjectWithUserRoles,
  UserStatus,
} from '@clef/shared/types/basic';
import { emptyGroups, isOrgOwner } from '@clef/shared/utils';
import { Column } from '@material-table/core';
import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  makeStyles,
  Menu,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@material-ui/core';
import HomeWorkIcon from '@material-ui/icons/HomeWorkOutlined';
import LockIcon from '@material-ui/icons/LockOutlined';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import SearchIcon from '@material-ui/icons/Search';
import { useSnackbar } from 'notistack';
import React, { useCallback, useMemo, useState } from 'react';
import { rootElement } from '../../../utils/dom_utils';
import { tableStyleOptions } from '../styles';
import { ChangeOwnershipDialog } from './ChangeOwnershipDialog';
import { ConfirmRemoveUserDialog } from './ConfirmRemoveUserDialog';
import { ConfirmTransferOwnershipDialog } from './ConfirmTransferOwnershipDialog';
import ManageProjectAccessDialog from './ManageProjectAccessDialog';
import { AccessMode, ConfirmVisibilityChangeDialog } from './ConfirmVisibilityChangeDialog';
import UserProfile, { DataItem } from '../components/UserProfile';
import OrgAdminDeleteProjectDialog from '@/components/Dialogs/OrgAdminDeleteProjectDialog';
import { format } from 'date-fns';
import {
  PROJECT_TYPE_ICON_LOOKUP,
  PROJECT_TYPE_NAME_LOOKUP,
} from '@/components/ProjectCard/NewProjectCard';
import { orgAdminUserTableStyles } from '@/components/OrgAdminProjectSettingsUserTable/orgAdminUserTableStyles';
import { useGetGroupsQuery } from '@/serverStore/account/queries';

export enum DialogPage {
  NONE,
  SELECT_NEW_OWNER,
  MANAGE_PROJECT_ACCESS,
  MANAGE_PROJECT_ACCESS_INVITE_MEMBERS,
  CONFIRM_TRANSFER_OWNERSHIP,
  CONFIRM_REMOVE_MEMBER,
  CONFIRM_DELETE_PROJECT,
  CONFIRM_CHANGE_VISIBILITY,
}

export const getProjectOwner = (project: Project, users: DataItem[]) => {
  if (project.usersRoles) {
    const owners = project.usersRoles
      .filter(role => role.role === ProjectRole.Owner)
      .map(role => users.find(user => user.id == role.userId))
      .filter(item => !!item);
    if (owners.length > 0) return owners[0];
  }
  return null;
};

export const useOrgAdminStyles = makeStyles(theme => ({
  orgSettingsPage: {
    maxWidth: 1200,
    margin: 'auto',
  },
  pageTitleContainer: {
    marginBottom: theme.spacing(5),
    display: 'flex',
  },
  titleText: {
    fontWeight: 500,
    marginBottom: theme.spacing(2),
  },
  companyText: {
    color: theme.palette.grey[600],
    fontWeight: 500,
  },
  searchIcon: {
    color: theme.palette.grey[500],
  },
  projectGridSearchInput: {
    padding: '9px 0 9px 14px',
  },
  projectGridSearchContainer: {
    borderRadius: 6,
    minWidth: 280,
    marginRight: 20,
    height: 34,
    backgroundColor: theme.palette.common.white,
  },
  tabLabel: {
    padding: theme.spacing(2, 3),
  },
  tabSelection: {
    color: 'Grey/200',
  },
  title: {
    color: theme.palette.grey[900],
  },
  moreVertIconButton: {
    maxWidth: 100,
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  projectDeleteLabel: {
    fontWeight: 'bold',
  },
  statsContainer: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  projectIconContainer: {
    width: 20,
    height: 20,
    marginRight: theme.spacing(2),
    flexShrink: 0,
    '& > img': {
      width: '100%',
      height: '100%',
    },
  },
}));

type SearchBarProps = {
  searchText: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
};
const SearchBar = (props: SearchBarProps) => {
  const styles = useOrgAdminStyles();
  const { searchText, onChange } = props;
  return (
    <OutlinedInput
      onClick={e => e.stopPropagation()}
      value={searchText}
      onChange={onChange}
      placeholder={t('Search projects')}
      endAdornment={<SearchIcon className={styles.searchIcon} />}
      inputProps={{
        className: styles.projectGridSearchInput,
      }}
      className={styles.projectGridSearchContainer}
    />
  );
};

const OrgAdmin = () => {
  const styles = useOrgAdminStyles();
  const tableStyles = orgAdminUserTableStyles();
  const loginUser = useTypedSelector(state => state.login.user);
  const users = useTypedSelector(state => state.user.users);
  const isOrgAdmin = isOrgOwner(loginUser?.userRole);
  const { data: allProjects, isLoading } = useOrgAdminGetProjectsQuery();
  const { data: projectStats, isLoading: isLoadingProjectStats } = useGetProjectStatsByOrgIdQuery();

  const { data: groups = emptyGroups } = useGetGroupsQuery({
    isEnabled: loginUser?.orgEnableGroup,
  });
  const groupUserById = useMemo(() => {
    return groups.reduce((acc, group) => {
      acc[group.id] = group;
      return acc;
    }, {} as Record<string, GroupUser>);
  }, [groups]);

  const userById = useMemo(() => {
    return users.reduce((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, {} as Record<string, AnyUser>);
  }, [users]);

  const [searchText, setSearchText] = useState<string>('');

  const { enqueueSnackbar } = useSnackbar();

  const {
    open: projectActionsMenuOpen,
    menuRef: projectActionsMenuRef,
    onMenuOpen: onProjectActionsMenuOpen,
    onMenuClose: onProjectActionsMenuClose,
  } = useMenu();

  const [filterOwnerStatus, setFilterOwnerStatus] = useState<'' | 'active' | 'inactive'>('');
  const [filterProjectAccess, setFilterProjectAccess] = useState<'' | 'public' | 'private'>('');
  const [filterProjectType, setFilterProjectType] = useState<LabelType | ''>('');

  const [selectedProjectId, setSelectedProjectId] = useState<ProjectId>();
  const { data: selectedProject } = useGetOrgAdminProjectDetailsQuery(selectedProjectId ?? 0);

  const [openDialog, setOpenDialog] = useState<DialogPage>();
  const [selectedUser, setCurrentlySelectedUser] = useState<AnyUser | null>(null);
  const [newAccessMode, setNewAccessMode] = useState<AccessMode>(AccessMode.Public);

  const renderedProjects = useMemo(() => {
    if (isLoading || isLoadingProjectStats) return [];
    let result = allProjects ?? [];
    if (searchText) {
      result = result.filter(project => {
        const ownerId =
          project.usersRoles?.find(role => role.role === ProjectRole.Owner)?.userId ?? '';
        const owner = userById[ownerId];
        const matchingText = [project.name, owner?.name, owner?.lastName, owner?.email].join(' ');
        return matchingText.toLowerCase().includes(searchText.toLowerCase());
      });
    }
    if (filterOwnerStatus) {
      result = result.filter(project => {
        const owner = getProjectOwner(project, users);
        if (!owner) return false;
        if (!('status' in owner)) return false;
        if (filterOwnerStatus === 'active') return owner.status === UserStatus.Active;
        if (filterOwnerStatus === 'inactive') return owner.status === UserStatus.Inactive;
        return true;
      });
    }
    if (filterProjectAccess) {
      result = result.filter(project => {
        if (filterProjectAccess === 'public') return !project.inviteOnly;
        if (filterProjectAccess === 'private') return project.inviteOnly;
        return true;
      });
    }
    if (filterProjectType) {
      result = result.filter(project => project.labelType === filterProjectType);
    }
    return result;
  }, [
    isLoading,
    isLoadingProjectStats,
    allProjects,
    searchText,
    filterOwnerStatus,
    filterProjectAccess,
    filterProjectType,
    users,
  ]);

  const columns: Column<ProjectWithUserRoles>[] = useMemo(() => {
    return [
      { title: 'Id', field: 'id', hidden: true },
      {
        title: 'Project',
        render: project => (
          <Box display="flex" alignItems="center">
            <Box className={styles.projectIconContainer}>
              {project.labelType && PROJECT_TYPE_ICON_LOOKUP[project.labelType]}
            </Box>
            <Typography>{project.name}</Typography>
          </Box>
        ),
        customSort: (a, b) => {
          const nameA = a.name;
          const nameB = b.name;
          return nameA.localeCompare(nameB);
        },
      },
      {
        title: t('Project owner'),
        width: 280,
        render: item => {
          const owner = getProjectOwner(item, users);
          return owner ? (
            <UserProfile item={owner} showUserStatus="inactive-only" />
          ) : (
            '<Unknown Owner>'
          );
        },
        customSort: (a, b) => {
          const ownerA = getProjectOwner(a, users);
          const ownerB = getProjectOwner(b, users);
          if (!ownerA && !ownerB) return 0;
          if (!ownerA) return 1;
          if (!ownerB) return -1;

          const name1 = 'name' in ownerA ? [ownerA.name, ownerA.lastName].join(' ') : '';
          const name2 = 'name' in ownerB ? [ownerB.name, ownerB.lastName].join(' ') : '';
          if (!name1 && !name2) {
            return ownerB.email.localeCompare(ownerA.email);
          }
          return name2.localeCompare(name1);
        },
      },
      {
        title: t('Images'),
        width: 50,
        render: item => projectStats?.mediaCountByDatasetId[item.datasetId ?? -1] ?? '0',
        customSort: (a, b) => {
          const countA = projectStats?.mediaCountByDatasetId[a.datasetId ?? -1] ?? 0;
          const countB = projectStats?.mediaCountByDatasetId[b.datasetId ?? -1] ?? 0;
          return countB - countA;
        },
      },
      {
        title: t('Classes'),
        width: 50,
        render: item => projectStats?.classCountByProjectId[item.id] ?? '0',
        customSort: (a, b) => {
          const countA = projectStats?.classCountByProjectId[a.id] ?? 0;
          const countB = projectStats?.classCountByProjectId[b.id] ?? 0;
          return countB - countA;
        },
      },
      {
        title: t('Models'),
        width: 50,
        render: item => projectStats?.modelCountByProjectId[item.id] ?? '0',
        customSort: (a, b) => {
          const countA = projectStats?.modelCountByProjectId[a.id] ?? 0;
          const countB = projectStats?.modelCountByProjectId[b.id] ?? 0;
          return countB - countA;
        },
      },
      {
        title: t('Created at'),
        width: 150,
        render: item => {
          const date = new Date(item.createdAt);
          return <Typography>{format(date, 'MMM d, yyyy')}</Typography>;
        },
        customSort: (a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime();
        },
      },
      {
        title: t('Access'),
        width: 250,
        align: 'center',
        sorting: false,
        render: item => {
          if (!item.inviteOnly) {
            return (
              <Box className={styles.statsContainer}>
                <HomeWorkIcon fontSize="small" />
                <Typography variant="body1">{t('Public to all')}</Typography>
              </Box>
            );
          }
          const userCount = item.usersRoles.filter(user => !groupUserById[user.userId]).length;
          const groupCount = item.usersRoles.filter(user => groupUserById[user.userId]).length;

          let userCountText = '';
          if (userCount > 1) userCountText = t('{{userCount}} users', { userCount });
          if (userCount === 1) userCountText = t('1 user');

          let groupCountText = '';
          if (groupCount > 1) groupCountText = t('{{groupCount}} groups', { groupCount });
          if (groupCount === 1) groupCountText = t('1 group');

          return (
            <Box className={styles.statsContainer} textAlign="left" whiteSpace="nowrap">
              <LockIcon fontSize="small" />
              <Typography variant="body1">
                {userCountText}
                {userCountText && groupCountText && ' & '}
                {groupCountText}
              </Typography>
            </Box>
          );
        },
      },
      {
        title: '',
        align: 'right',
        sorting: false,
        width: 81,
        render: project => {
          return (
            <IconButton
              id="org-settings-members-more-vert-button"
              data-testid="org-settings-members-more-vert-button"
              onClick={e => {
                onProjectActionsMenuOpen(e);
                setSelectedProjectId(project.id);
              }}
              className={styles.moreVertIconButton}
            >
              <MoreVertIcon />
            </IconButton>
          );
        },
      },
    ];
  }, [
    groupUserById,
    onProjectActionsMenuOpen,
    projectStats?.classCountByProjectId,
    projectStats?.mediaCountByDatasetId,
    projectStats?.modelCountByProjectId,
    styles.moreVertIconButton,
    styles.projectIconContainer,
    styles.statsContainer,
    users,
  ]);

  const removeUserFromProject = useCallback(async () => {
    if (!selectedUser || !selectedProject) return;
    try {
      await deleteUserFromProjectApi(selectedProject.orgId, selectedProject.id, selectedUser.id);
      enqueueSnackbar('User removed from project successfully', {
        variant: 'success',
      });
      setOpenDialog(DialogPage.NONE);
    } catch {
      enqueueSnackbar('Failed to remove user from project', {
        variant: 'error',
      });
    }
  }, [enqueueSnackbar, selectedProject, selectedUser]);

  const transferOwnershipOfProject = useCallback(async () => {
    if (!selectedUser || !selectedProject) return;
    try {
      await transferProjectOwnershipApi(selectedProject.orgId, selectedProject.id, selectedUser.id);
      enqueueSnackbar('Ownership transferred successfully', {
        variant: 'success',
      });
      setOpenDialog(DialogPage.NONE);
    } catch {
      enqueueSnackbar('Failed to transfer ownership', {
        variant: 'error',
      });
    }
  }, [enqueueSnackbar, selectedProject, selectedUser]);

  const changeProjectVisibility = useCallback(async () => {
    if (!selectedProject) return;
    try {
      await changeProjectVisibilityApi(
        selectedProject.orgId,
        selectedProject.id,
        newAccessMode === AccessMode.Private,
      );
      enqueueSnackbar('Changed project access level', {
        variant: 'success',
      });
      setOpenDialog(DialogPage.NONE);
    } catch {
      enqueueSnackbar('Failed to change project access level', {
        variant: 'error',
      });
    }
  }, [enqueueSnackbar, newAccessMode, selectedProject]);

  if (!isOrgAdmin) return <h1>You are not authorized to view this page!</h1>;

  if (isLoading) {
    return <LoadingProgress />;
  }

  return (
    <Box>
      <Box marginTop={8} className={tableStyles.projectManagementHeader}>
        <SearchBar
          searchText={searchText}
          onChange={({ target: { value } }) => {
            setSearchText(value);
          }}
        />
        <Box className={tableStyles.filterContainer}>
          <FormControl variant="standard">
            <InputLabel className={tableStyles.filterControlLabel}>{t('Owner status')}</InputLabel>
            <Select
              label="Owner status"
              value={filterOwnerStatus}
              onChange={e => setFilterOwnerStatus(e.target.value as '' | 'active' | 'inactive')}
              className={tableStyles.filterControl}
              MenuProps={{
                getContentAnchorEl: null,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
              }}
            >
              <MenuItem value="">
                <Typography>
                  <em>{t('Any')}</em>
                </Typography>
              </MenuItem>
              <MenuItem value="active">
                <Typography>{t('Active')}</Typography>
              </MenuItem>
              <MenuItem value="inactive">
                <Typography>{t('Inactive')}</Typography>
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="standard">
            <InputLabel className={tableStyles.filterControlLabel}>
              {t('Project access')}
            </InputLabel>
            <Select
              label="Project access"
              value={filterProjectAccess}
              onChange={e => setFilterProjectAccess(e.target.value as '' | 'public' | 'private')}
              className={tableStyles.filterControl}
              MenuProps={{
                getContentAnchorEl: null,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
              }}
            >
              <MenuItem value="">
                <Typography>
                  <em>{t('Any')}</em>
                </Typography>
              </MenuItem>
              <MenuItem value="public">
                <Typography>{t('Public')}</Typography>
              </MenuItem>
              <MenuItem value="private">
                <Typography>{t('Private')}</Typography>
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="standard">
            <InputLabel className={tableStyles.filterControlLabel}>{t('Project type')}</InputLabel>
            <Select
              label="Project type"
              value={filterProjectType}
              onChange={e => setFilterProjectType(e.target.value as LabelType)}
              className={tableStyles.filterControl}
              MenuProps={{
                getContentAnchorEl: null,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
              }}
            >
              <MenuItem value="">
                <Typography>
                  <em>{t('Any')}</em>
                </Typography>
              </MenuItem>
              {Object.values(LabelType).map(element => (
                <MenuItem key={element} value={element}>
                  <Typography>{t(PROJECT_TYPE_NAME_LOOKUP[element as LabelType])}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box marginTop={8} flex={true} flexDirection={'column'}>
        {/* Options menu for each project */}
        <Menu
          open={projectActionsMenuOpen}
          anchorEl={projectActionsMenuRef}
          onClose={onProjectActionsMenuClose}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          container={rootElement}
        >
          <MenuItem
            key={`change ownership of ${selectedProject?.id}`}
            onClick={() => {
              setOpenDialog(DialogPage.SELECT_NEW_OWNER);
              onProjectActionsMenuClose();
            }}
            button
          >
            <Typography>{t('Change Ownership')}</Typography>
          </MenuItem>
          <MenuItem
            key={`manage access to ${selectedProject?.id}`}
            onClick={() => {
              setOpenDialog(DialogPage.MANAGE_PROJECT_ACCESS);
              onProjectActionsMenuClose();
            }}
            button
          >
            <Typography>{t('Manage Project Access')}</Typography>
          </MenuItem>
          <MenuItem
            key={`add members to ${selectedProject?.id}`}
            onClick={() => {
              setOpenDialog(DialogPage.MANAGE_PROJECT_ACCESS_INVITE_MEMBERS);
              onProjectActionsMenuClose();
            }}
            button
          >
            <Typography>{t('Add Members to Project')}</Typography>
          </MenuItem>
          <Divider className={styles.divider} />
          <MenuItem
            key={'delete-project-button'}
            onClick={() => {
              setOpenDialog(DialogPage.CONFIRM_DELETE_PROJECT);
              onProjectActionsMenuClose();
            }}
            button
          >
            <Typography color="secondary" className={styles.projectDeleteLabel}>
              {t('Delete Project')}
            </Typography>
          </MenuItem>
        </Menu>

        <MatTable<ProjectWithUserRoles>
          title=""
          data={renderedProjects}
          columns={columns}
          options={{
            ...tableStyleOptions,
            search: false,
            toolbar: false,
            padding: 'dense',
          }}
        />

        {selectedProject && (
          <ChangeOwnershipDialog
            open={openDialog == DialogPage.SELECT_NEW_OWNER}
            project={selectedProject}
            onClose={() => {
              if (openDialog == DialogPage.SELECT_NEW_OWNER) setOpenDialog(DialogPage.NONE);
            }}
          />
        )}

        {selectedProject && (
          <ManageProjectAccessDialog
            onUserRemovalStart={(user: AnyUser) => {
              setOpenDialog(DialogPage.CONFIRM_REMOVE_MEMBER);
              setCurrentlySelectedUser(user);
            }}
            openConfirmationDialog={(confirmation: DialogPage) => setOpenDialog(confirmation)}
            onTransferOwnershipStart={(user: AnyUser) => {
              setOpenDialog(DialogPage.CONFIRM_TRANSFER_OWNERSHIP);
              setCurrentlySelectedUser(user);
            }}
            onVisibilityChangeStart={(newAccessMode: AccessMode) => {
              setOpenDialog(DialogPage.CONFIRM_CHANGE_VISIBILITY);
              setNewAccessMode(newAccessMode);
            }}
            open={
              openDialog == DialogPage.MANAGE_PROJECT_ACCESS ||
              openDialog == DialogPage.MANAGE_PROJECT_ACCESS_INVITE_MEMBERS
            }
            defaultTab={
              openDialog == DialogPage.MANAGE_PROJECT_ACCESS_INVITE_MEMBERS
                ? 'invite-members'
                : 'manage-access'
            }
            projectId={selectedProject.id}
            onCancel={() => {
              if (
                openDialog == DialogPage.MANAGE_PROJECT_ACCESS ||
                openDialog == DialogPage.MANAGE_PROJECT_ACCESS_INVITE_MEMBERS
              )
                setOpenDialog(DialogPage.NONE);
            }}
          />
        )}

        {selectedProject && (
          <OrgAdminDeleteProjectDialog
            open={openDialog == DialogPage.CONFIRM_DELETE_PROJECT}
            project={selectedProject}
            handleClose={() => {
              if (openDialog == DialogPage.CONFIRM_DELETE_PROJECT) setOpenDialog(DialogPage.NONE);
            }}
          />
        )}

        {selectedProject && (
          <ConfirmRemoveUserDialog
            open={openDialog == DialogPage.CONFIRM_REMOVE_MEMBER}
            user={selectedUser ?? undefined}
            selectedProject={selectedProject}
            onCancel={() => {
              if (openDialog == DialogPage.CONFIRM_REMOVE_MEMBER) setOpenDialog(DialogPage.NONE);
            }}
            onConfirm={removeUserFromProject}
          />
        )}

        {selectedProject && (
          <ConfirmTransferOwnershipDialog
            open={openDialog == DialogPage.CONFIRM_TRANSFER_OWNERSHIP}
            user={selectedUser ?? undefined}
            selectedProject={selectedProject}
            onCancel={() => {
              if (openDialog == DialogPage.CONFIRM_TRANSFER_OWNERSHIP)
                setOpenDialog(DialogPage.NONE);
            }}
            onConfirm={transferOwnershipOfProject}
          />
        )}

        {selectedProject && (
          <ConfirmVisibilityChangeDialog
            open={openDialog == DialogPage.CONFIRM_CHANGE_VISIBILITY}
            accessMode={newAccessMode}
            onCancel={() => {
              if (openDialog == DialogPage.CONFIRM_CHANGE_VISIBILITY)
                setOpenDialog(DialogPage.NONE);
            }}
            onConfirm={changeProjectVisibility}
          />
        )}
      </Box>
    </Box>
  );
};

export default OrgAdmin;
